export const MOTIVATION_MODE = "motivation";
export const COEFFICIENT_MODE = "coefficient";

const COEFFICIENT_MIN = 0;
const COEFFICIENT_MAX = 1.5;
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

export default class IndividualCoefficientsService {
    // защита от "пустышки" вместо заявки: бэк может ответить 200 с нулевым uid
    // и обнулёнными полями вместо ошибки (например если version отсутствовал в запросе) —
    // такой ответ нельзя принимать как успех, иначе он затирает локальное состояние
    static isValidRequestResponse(data, expectedUid) {
        if (!data || !data.uid || data.uid === EMPTY_GUID) return false;
        if (expectedUid && data.uid !== expectedUid) return false;
        return true;
    }

    // Number.isFinite вместо "Number(value) || 0" — Infinity проходит через "||" как truthy,
    // и при слишком большом введённом числе (или ошибочных данных с бэка) считался бы дальше,
    // разваливая расчёты в NaN/Infinity
    static toKopecks(value) {
        const number = Number(value);
        return Number.isFinite(number) ? Math.round(number * 100) : 0;
    }

    static fromKopecks(kopecks) {
        return kopecks / 100;
    }

    static roundMoney(value) {
        return this.fromKopecks(this.toKopecks(value));
    }

    static roundCoefficient(value) {
        const number = Number(value);
        return Number.isFinite(number) ? Math.round(number * 100) / 100 : 0;
    }

    static recalculateRow(row, mode) {
        const baseKop = this.toKopecks(row.baseBonusAmount);

        if (mode === COEFFICIENT_MODE) {
            const calculatedKop = Math.round(baseKop * (Number(row.individualCoefficient) || 0));
            return {
                ...row,
                calculatedAmount: this.fromKopecks(calculatedKop),
                motivationAmount: this.fromKopecks(calculatedKop - baseKop),
            };
        }

        const motivationKop = this.toKopecks(row.motivationAmount);
        const calculatedKop = baseKop + motivationKop;
        return {
            ...row,
            calculatedAmount: this.fromKopecks(calculatedKop),
            individualCoefficient: baseKop
                ? this.roundCoefficient(this.fromKopecks(calculatedKop) / this.fromKopecks(baseKop))
                : 0,
        };
    }

    static calculateTotals(rows) {
        const totalsKop = (rows || []).reduce((acc, row) => {
            acc.baseBonusAmount += this.toKopecks(row.baseBonusAmount);
            acc.motivationAmount += this.toKopecks(row.motivationAmount);
            acc.calculatedAmount += this.toKopecks(row.calculatedAmount);
            return acc;
        }, { baseBonusAmount: 0, motivationAmount: 0, calculatedAmount: 0 });

        return {
            baseBonusAmount: this.fromKopecks(totalsKop.baseBonusAmount),
            motivationAmount: this.fromKopecks(totalsKop.motivationAmount),
            calculatedAmount: this.fromKopecks(totalsKop.calculatedAmount),
        };
    }

    static isCoefficientOutOfRange(coefficient) {
        return coefficient === null ||
            coefficient === undefined ||
            coefficient < COEFFICIENT_MIN ||
            coefficient > COEFFICIENT_MAX;
    }

    static isTotalsInvalid(totals) {
        return this.toKopecks(totals.calculatedAmount) > this.toKopecks(totals.baseBonusAmount);
    }

    static isRowExceedingBase(row) {
        return this.toKopecks(row.calculatedAmount) > this.toKopecks(row.baseBonusAmount);
    }

    // значение поля не готово к отправке: пусто, промежуточное состояние набора ("-", "1.")
    // или не приводится к числу
    static isValueIncomplete(value) {
        if (value === null || value === undefined || value === "" || value === "-") return true;
        if (typeof value === "string" && value.endsWith(".")) return true;
        return Number.isNaN(Number(value));
    }

    static hasUnfilledRows(rows) {
        return (rows || []).some((row) =>
            this.isValueIncomplete(row.motivationAmount) || this.isValueIncomplete(row.individualCoefficient));
    }

    static classifyError(error) {
        const status = error.response?.status;
        const data = error.response?.data;
        const type = data?.type;
        const detail = data?.detail;

        if (status === 401) {
            return { kind: "unauthorized", message: detail };
        }
        if (status === 403) {
            return { kind: "forbidden", message: detail || "У вас нет доступа к этому действию." };
        }
        if (status === 404) {
            return { kind: "notFound", message: detail || "Заявка не найдена." };
        }
        if (status === 409 && type === "/problem-types/individual-coefficient-event-unavailable") {
            return { kind: "eventUnavailable", message: detail };
        }
        if (status === 409 && type === "/problem-types/request-not-draft") {
            return { kind: "notDraft", message: detail || "Заявка больше не в статусе черновика." };
        }
        if (status === 409) {
            return { kind: "versionConflict", message: detail || "Заявка была изменена. Загрузите актуальную версию." };
        }
        // бизнес-проверка (validate/submit) на провал отдаёт 400 со списком
        // details: [{ message, block, ... }] — тот же формат, что ждёт NotificationWindowRequests
        if (Array.isArray(data?.details) && data.details.length > 0) {
            return { kind: "businessValidation", details: data.details };
        }
        if (status === 422) {
            return { kind: "validation", errors: data?.errors };
        }
        // 5xx приходит как ProblemDetails без detail (только generic title вида
        // "An error occurred while processing your request.") — его показывать пользователю нельзя
        if (status >= 500) {
            return { kind: "serverError", message: detail || "Что-то пошло не так. Повторите попытку позже." };
        }
        return { kind: "unknown", message: detail || "Не удалось выполнить запрос. Попробуйте ещё раз." };
    }

    // приводит список бизнес-ошибок из ответа бэка к формату NotificationWindowRequests
    static toNotificationDetails(details) {
        return (details || [])
            .map((item) => ({
                block: item.block ?? 1,
                message: item.message || item.description || "",
            }))
            .filter((item) => item.message);
    }

    static parseProblemJsonErrors(errors) {
        const rowErrors = {};
        const totalsErrors = [];

        if (errors) {
            Object.entries(errors).forEach(([key, messages]) => {
                const rowMatch = key.match(/^rows\[(\d+)]\.(\w+)$/);
                if (rowMatch) {
                    const [, index, field] = rowMatch;
                    rowErrors[index] = { ...rowErrors[index], [field]: messages[0] };
                } else {
                    totalsErrors.push(...messages);
                }
            });
        }

        return { rowErrors, totalsErrors };
    }
}